/**
 * 响应式适配模块
 * 统一管理设备检测、rem 适配、窗口事件监听等功能
 */
import { ref, computed, Ref } from 'vue';

// ==================== 类型定义 ====================
export type DeviceType = 'pc' | 'mobile' | 'tablet' | 'unknown';
export type LayoutMode = 'pc' | 'mobile';

export const LAYOUT_BREAKPOINT = 750;
const RESIZE_DEBOUNCE_DELAY = 80;

type EventCallback = () => void;

interface RemConfig {
  desktopFontSize: number;
  mobileMinFontSize: number;
  mobileMinWidth: number;
}

// ==================== 事件管理器 ====================
/**
 * 全局事件管理器
 * 用于统一管理 window 事件监听，避免重复监听
 */
class GlobalEventManager {
  private resizeCallbacks: Set<EventCallback> = new Set();
  private orientationCallbacks: Set<EventCallback> = new Set();
  private resizeTimer: ReturnType<typeof setTimeout> | null = null;
  private isResizeListenerAttached = false;
  private isOrientationListenerAttached = false;

  /**
   * 订阅 resize 事件
   * @param callback 回调函数
   * @returns 取消订阅的函数
   */
  onResize(callback: EventCallback): () => void {
    this.resizeCallbacks.add(callback);

    if (!this.isResizeListenerAttached && typeof window !== 'undefined') {
      window.addEventListener('resize', this.handleResize);
      this.isResizeListenerAttached = true;
    }

    return () => {
      this.resizeCallbacks.delete(callback);
      if (this.resizeCallbacks.size === 0 && this.isResizeListenerAttached) {
        window.removeEventListener('resize', this.handleResize);
        this.isResizeListenerAttached = false;
        if (this.resizeTimer) {
          clearTimeout(this.resizeTimer);
          this.resizeTimer = null;
        }
      }
    };
  }

  /**
   * 订阅 orientationchange 事件
   * @param callback 回调函数
   * @returns 取消订阅的函数
   */
  onOrientationChange(callback: EventCallback): () => void {
    this.orientationCallbacks.add(callback);

    if (!this.isOrientationListenerAttached && typeof window !== 'undefined') {
      window.addEventListener('orientationchange', this.handleOrientationChange);
      this.isOrientationListenerAttached = true;
    }

    return () => {
      this.orientationCallbacks.delete(callback);
      if (this.orientationCallbacks.size === 0 && this.isOrientationListenerAttached) {
        window.removeEventListener('orientationchange', this.handleOrientationChange);
        this.isOrientationListenerAttached = false;
      }
    };
  }

  private handleResize = () => {
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    this.resizeTimer = setTimeout(() => {
      this.resizeCallbacks.forEach(callback => callback());
    }, RESIZE_DEBOUNCE_DELAY);
  };

  private handleOrientationChange = () => {
    setTimeout(() => {
      this.orientationCallbacks.forEach(callback => callback());
    }, 100);
  };
}

// 创建全局单例
const eventManager = new GlobalEventManager();

// ==================== Rem 适配管理器 ====================
/**
 * Rem 适配管理器
 * 根据屏幕宽度动态调整根字体大小
 */
class RemManager {
  private config: RemConfig | null = null;
  private unsubscribeResize: (() => void) | null = null;
  private unsubscribeOrientation: (() => void) | null = null;

  init(options?: Partial<RemConfig>) {
    // 重复初始化时先移除旧订阅，避免 HMR 或测试环境累积回调。
    this.destroy();
    this.config = Object.assign(
      {
        desktopFontSize: 14,
        mobileMinFontSize: 11,
        mobileMinWidth: 320,
      },
      options || {}
    );

    this.update();

    // rem 与布局模式共用同一个 resize 调度，避免两种状态短暂错位。
    this.unsubscribeResize = eventManager.onResize(() => {
      this.update();
    });

    // 订阅 orientationchange 事件
    this.unsubscribeOrientation = eventManager.onOrientationChange(() => {
      this.update();
    });
  }

  private update() {
    if (!this.config || typeof document === 'undefined') return;

    const docEl = document.documentElement;
    let w = docEl.clientWidth || window.innerWidth;
    if (!w) w = LAYOUT_BREAKPOINT;

    if (w >= LAYOUT_BREAKPOINT) {
      docEl.style.fontSize = this.config.desktopFontSize + 'px';
      return;
    }

    // 移动端在最小视口与布局断点之间连续插值，避免断点处字号跳变。
    const mobileWidth = Math.max(this.config.mobileMinWidth, w);
    const mobileRange = LAYOUT_BREAKPOINT - this.config.mobileMinWidth;
    const progress = mobileRange > 0 ? (mobileWidth - this.config.mobileMinWidth) / mobileRange : 1;
    const fs = this.config.mobileMinFontSize
      + (this.config.desktopFontSize - this.config.mobileMinFontSize) * Math.min(1, progress);

    docEl.style.fontSize = fs + 'px';
  }

  destroy() {
    if (this.unsubscribeResize) {
      this.unsubscribeResize();
      this.unsubscribeResize = null;
    }
    if (this.unsubscribeOrientation) {
      this.unsubscribeOrientation();
      this.unsubscribeOrientation = null;
    }
    this.config = null;
  }
}

const remManager = new RemManager();

/**
 * 初始化 rem
 * @param options 配置选项
 */
export function initRem(options?: Partial<RemConfig>) {
  remManager.init(options);
}

// ==================== 布局模式管理器 ====================
/**
 * 布局模式只由视口宽度决定，不替代真实设备、OS 或宿主能力判断。
 */
class LayoutModeManager {
  private layoutMode: Ref<LayoutMode>;
  private unsubscribeResize: (() => void) | null = null;
  private unsubscribeOrientation: (() => void) | null = null;

  constructor() {
    this.layoutMode = ref(this.getLayoutMode());
    this.updateClass();
    this.startListening();
  }

  /**
   * 获取当前设备类型（响应式）
   */
  getMode(): Ref<LayoutMode> {
    return this.layoutMode;
  }

  /**
   * 更新设备类型
   */
  private getLayoutMode(): LayoutMode {
    if (typeof window === 'undefined' || typeof document === 'undefined') return 'pc';
    const width = document.documentElement.clientWidth || window.innerWidth;
    return width < LAYOUT_BREAKPOINT ? 'mobile' : 'pc';
  }

  private updateLayoutMode() {
    this.layoutMode.value = this.getLayoutMode();
    this.updateClass();
  }

  /**
   * 更新 DOM class
   */
  private updateClass() {
    if (typeof document === 'undefined') return;
    if (this.layoutMode.value === 'mobile') {
      document.documentElement.classList.add('mb');
    } else {
      document.documentElement.classList.remove('mb');
    }
  }

  /**
   * 开始监听事件
   */
  private startListening() {
    // 订阅 resize 事件
    this.unsubscribeResize = eventManager.onResize(() => {
      this.updateLayoutMode();
    });

    // 订阅 orientationchange 事件
    this.unsubscribeOrientation = eventManager.onOrientationChange(() => {
      this.updateLayoutMode();
    });
  }

  /**
   * 停止监听事件
   */
  stopListening() {
    if (this.unsubscribeResize) {
      this.unsubscribeResize();
      this.unsubscribeResize = null;
    }
    if (this.unsubscribeOrientation) {
      this.unsubscribeOrientation();
      this.unsubscribeOrientation = null;
    }
  }
}

const layoutModeManager = new LayoutModeManager();

/**
 * 自动监听窗口大小和设备方向变化，返回响应式的布局模式
 * 
 * @example
 * ```ts
 * const { layoutMode } = useLayoutMode()
 * // layoutMode 是响应式的，会自动更新
 * ```
 */
export function useLayoutMode() {
  const mode = layoutModeManager.getMode();
  const layoutMode = computed(() => mode.value);

  return {
    layoutMode,
  };
}

// 保留旧入口，避免未迁移调用方在本轮全站适配中失效。
export function useDeviceDetection() {
  const { layoutMode } = useLayoutMode();
  return {
    layoutMode,
  };
}
